import { useRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo } from 'react'
import styled from 'styled-components'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { ModalElementContext } from 'contexts/ModalProvider'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListItem from 'LuxKit/List/ListItem'
import config from 'constants/config'
import TextButton from 'LuxKit/Button/TextButton'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { last, sum } from 'lib/array/arrayUtils'
import { fetchAvailableCredit } from 'actions/AuthActions'
import CurrencyContext from 'contexts/currencyContext'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { removeRedeemedGiftCard } from 'actions/GiftCardActions'

import type { RedeemedCard } from './common'
import CurrencyMismatchWarning from './CurrencyMismatchWarning'
import { FormattedMessage, useIntl } from 'react-intl'

const ListItemContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const TotalContainer = styled.div`
  flex: 1;
`

interface Props {
  redeemedCards: Array<RedeemedCard>
  onRedeemAnother: () => void
  checkoutCurrency?: string
}

function Success({ redeemedCards, onRedeemAnother, checkoutCurrency }: Props) {
  const isGiftCardV2 = config.ENABLE_GIFT_CARD_V2
  const modalElement = useContext(ModalElementContext)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const modalBodyRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()

  const currencyCode = last(redeemedCards)?.currencyCode

  const dispatch = useAppDispatch()

  const creditsLedger = useAppSelector((state) => state.auth.account.creditsLedger) ?? []
  const creditBalance = useAppSelector((state) => state.auth.account.creditsByCurrency[currencyCode])

  let expiresAt: string | undefined
  creditsLedger.forEach(credit => {
    if (!expiresAt || credit.expiresAt > expiresAt) {
      expiresAt = credit.expiresAt
    }
  })

  const total = sum(redeemedCards.filter(card => card.currencyCode === currencyCode), card => parseFloat(String(card.amount)))

  // Fetch credit balance
  useEffect(() => {
    if (currencyCode) {
      dispatch(fetchAvailableCredit(currencyCode))
    }
  }, [dispatch, currencyCode])

  useLayoutEffect(() => {
    if (modalBodyRef.current?.scrollTo) {
      // Scroll to bottom so the latest card and the "Redeem another card" button are visible
      modalBodyRef.current.scrollTo(0, modalBodyRef.current.scrollHeight)
    }
  }, [])

  const close = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const image = config.BRAND === 'luxuryescapes' ? getSVGImageUrl('payments-lecredit') : undefined

  const mismatchedCurrencies = useMemo(() => (
    checkoutCurrency ?
        [...new Set(
          redeemedCards
            .map(card => card.currencyCode)
            .filter(currency => currency !== checkoutCurrency),
        )] :
        []
  ), [checkoutCurrency, redeemedCards])

  const CardEndIcon = useCallback(({ code }: { code: string }) => {
    const handleRemoveCard = () => {
      dispatch(removeRedeemedGiftCard(code))
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{code}</span>
        <button
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleRemoveCard}
          aria-label={intl.formatMessage({
            id: 'Si7cQE5jcV8HXAEmHn6m',
            defaultMessage: 'Remove card',
            description: 'Aria label for remove gift card button on the gift card purchase or redemption page',
          })}
        >
          <LineTimesIcon />
        </button>
      </div>
    )
  }, [dispatch, intl])

  return (
    isGiftCardV2 && !isSpoofed ? (
      <>
        <ModalHeader
          title={intl.formatMessage({
            id: 'yR8fLikmT3t9VZY8UE7V',
            defaultMessage: 'Gift Card added',
            description: 'Modal title when gift card is successfully added in the gift card section',
          })}
          onCloseButtonClick={close}
        />
        <ModalBody ref={modalBodyRef}>
          <ModalContent>
            <VerticalSpacer gap={24}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'aUpLRCIJqohBiunr2vM5',
                  defaultMessage: 'Available at checkout',
                  description: 'Message indicating gift card is available at checkout',
                })}
              </BodyText>
              <VerticalSpacer gap={8}>
                <div>
                  {redeemedCards.map((card) => {
                    return <ListItemContainer key={card.code}>
                      <ListItem
                        title={<BodyText variant="large" weight="bold"><FormatCurrency currency={card.currencyCode} value={card.amount} format="preciseDollar" /></BodyText>}
                        startIcon={image && <img src={image} width="34" height="24" alt="" />}
                        endIcon={<CardEndIcon code={card.code} />}
                      />
                    </ListItemContainer>
                  })}
                </div>

              </VerticalSpacer>

              {mismatchedCurrencies.length > 0 && (
                <CurrencyMismatchWarning checkoutCurrency={checkoutCurrency!} mismatchedCurrencies={mismatchedCurrencies} />
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: 'GhWe2WDX3D14_vA2B39D',
              defaultMessage: 'Done',
              description: 'Button text labelled done to close the current modal dialog after completing action',
            }),
            onClick: close,
          }}
        >
          <TotalContainer>
            <CurrencyContext.Provider value={currencyCode}>
              <Heading variant="heading4" wrap="no-wrap">
                <FormatCurrency currency={currencyCode} value={total} /> <Caption variant="large" as="span">
                  {intl.formatMessage({
                    id: 'G82Q-FbAYbOFQ69XiO4N',
                    defaultMessage: 'total',
                    description: 'Caption label for the total amount displayed in the offer pricing summary card',
                  })}
                </Caption>
              </Heading>
            </CurrencyContext.Provider>
          </TotalContainer>
          <TextButton kind="tertiary" onClick={onRedeemAnother}>
            {intl.formatMessage({
              id: 'TQ5TbrYK3zbxK2xOjoD2',
              defaultMessage: 'Add another card',
              description: 'Button text to add another gift card on the gift card purchase or redemption page',
            })}
          </TextButton>
        </ModalFooter>
      </>
    ) : (
      <>
        <ModalHeader
          title={intl.formatMessage({
            id: 'OwsF896Uo7-IUCU4nxrv',
            defaultMessage: 'Gift credit summary',
            description: 'Modal title for gift credit summary on the gift card purchase or redemption page',
          })}
          onCloseButtonClick={close}
        />
        <ModalBody ref={modalBodyRef}>
          <ModalContent>
            <VerticalSpacer gap={24}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: '74neqXqYGuW9dXwUy3_Z',
                  defaultMessage: 'Here is a summary of redeemed {brandName} gift cards',
                  description: 'Description text for gift credit summary on the gift card page',
                }, {
                  brandName: config.brandConfig.name,
                })}
              </BodyText>

              <VerticalSpacer gap={8}>
                <div>
                  {redeemedCards.map((card, i) => {
                    const isLast = i === redeemedCards.length - 1
                    let title
                    if (isLast) {
                      title = (
                        <BodyText variant="large" weight="bold">
                          {intl.formatMessage({
                            id: 'm3NyOVFGAJhz56RD2Nal',
                            defaultMessage: 'Gift card redeemed!',
                            description: 'Title for last redeemed gift card on the gift card purchase or redemption page',
                          })}
                        </BodyText>
                      )
                    } else {
                      title = intl.formatMessage({
                        id: '3oOrI7145OKWMk22l7hd',
                        defaultMessage: 'Gift card redeemed',
                        description: 'Success title displayed after a gift card has been successfully redeemed',
                      })
                    }
                    return <ListItemContainer key={card.code}>
                      <ListItem
                        title={title}
                        subtitle={card.code}
                        startIcon={image && <img src={image} width="34" height="24" alt="" />}
                        endIcon={<FormatCurrency currency={card.currencyCode} value={card.amount} format="preciseDollar" />}
                      />
                    </ListItemContainer>
                  })}
                </div>

                <TextButton kind="tertiary" onClick={onRedeemAnother} startIcon={<LinePlusIcon />}>
                  {intl.formatMessage({
                    id: 'Er9M-B4oyvKoKHpT0JlC',
                    defaultMessage: 'Redeem another card',
                    description: 'Button text to redeem another gift card on the gift card purchase or redemption page',
                  })}
                </TextButton>
              </VerticalSpacer>

              {mismatchedCurrencies.length > 0 && (
                <CurrencyMismatchWarning checkoutCurrency={checkoutCurrency!} mismatchedCurrencies={mismatchedCurrencies} />
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: 'TGq2URqPBZwNQT1zJVAs',
              defaultMessage: 'Done',
              description: 'Button text to close the currently open modal dialog and return to the page',
            }),
            onClick: close,
          }}
        >
          <TotalContainer>
            <CurrencyContext.Provider value={currencyCode}>
              <Heading variant="heading4" wrap="no-wrap">
                <FormattedMessage
                  id="h1mmHJr8Vgcn_vqRyLxc"
                  defaultMessage="{amount} <caption>added</caption>"
                  description="Total amount of gift cards added with label on the gift card page"
                  values={{
                    amount: <FormatCurrency currency={currencyCode} value={total} />,
                    caption: (chunks) => <Caption variant="large" as="span">{chunks}</Caption>,
                  }}
                />
              </Heading>
              {creditBalance?.loading && <Caption variant="large" colour="neutral-three">
                {intl.formatMessage({
                  id: 'vsLHva_jBmUP4vdRWzfX',
                  defaultMessage: 'Fetching credit balance...',
                  description: 'Loading indicator text while fetching credit balance',
                })}
              </Caption>}
              {typeof creditBalance?.balance === 'number' && !creditBalance.loading && <Caption variant="large" colour="neutral-three">
                {!!expiresAt && <FormattedMessage
                  id="bo8Rxz0BW8PDfPIjIYLb"
                  defaultMessage="{creditBalance} credit balance (Expires {expiryDate})"
                  description="Credit balance amount with label and expiry date in the gift card section"
                  values={{
                    creditBalance: <FormatCurrency currency={currencyCode} value={creditBalance.balance} />,
                    expiryDate: <FormatDateTime value={expiresAt} format="dmy-casual-short" />,
                  }}
                />}
                {!expiresAt && <FormattedMessage
                  id="nz6qP2uWy2dIRpEOGsMq"
                  defaultMessage="{creditBalance} credit balance"
                  description="Credit balance amount with label in the gift card section"
                  values={{ creditBalance: <FormatCurrency currency={currencyCode} value={creditBalance.balance} /> }}
                />}
              </Caption>}
            </CurrencyContext.Provider>
          </TotalContainer>
        </ModalFooter>
      </>
    )
  )
}

export default Success
